import { useEffect, useRef, useState } from 'react'
import { api } from '../api'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential?: string }) => void
            auto_select?: boolean
            cancel_on_tap_outside?: boolean
            use_fedcm_for_prompt?: boolean
          }) => void
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: string
              theme?: string
              size?: string
              text?: string
              shape?: string
              width?: number
              logo_alignment?: string
              locale?: string
            },
          ) => void
        }
      }
    }
  }
}

const GIS_SCRIPT = 'https://accounts.google.com/gsi/client'

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve()

  const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT}"]`)
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) {
        resolve()
        return
      }
      const done = () => {
        if (window.google?.accounts?.id) resolve()
        else reject(new Error('Google script failed'))
      }
      existing.addEventListener('load', done, { once: true })
      existing.addEventListener('error', () => reject(new Error('Google script failed')), {
        once: true,
      })
      // Script already complete (cached): 'load' will not fire again.
      if (existing.getAttribute('data-loaded') === '1' || existing.dataset.loaded === '1') {
        done()
        return
      }
      // readyState for classic scripts
      const rs = (existing as HTMLScriptElement & { readyState?: string }).readyState
      if (rs === 'complete' || rs === 'loaded') {
        existing.setAttribute('data-loaded', '1')
        done()
      }
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = GIS_SCRIPT
    script.async = true
    script.defer = true
    script.onload = () => {
      script.setAttribute('data-loaded', '1')
      resolve()
    }
    script.onerror = () => reject(new Error('Google script failed'))
    document.head.appendChild(script)
  })
}

type Props = {
  mode?: 'signup' | 'signin'
  disabled?: boolean
  /** Where to place the "or" divider relative to the Google button. */
  divider?: 'above' | 'below' | 'none'
  onCredential: (credential: string) => void | Promise<void>
  onError?: (message: string) => void
}

export function GoogleSignInButton({
  mode = 'signup',
  disabled = false,
  divider = 'below',
  onCredential,
  onError,
}: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const [clientId, setClientId] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const callbackRef = useRef(onCredential)
  const errorRef = useRef(onError)

  useEffect(() => {
    callbackRef.current = onCredential
  }, [onCredential])

  useEffect(() => {
    errorRef.current = onError
  }, [onError])

  useEffect(() => {
    let cancelled = false
    async function loadConfig() {
      try {
        const config = await api.googleAuthConfig()
        if (cancelled) return
        if (!config.enabled || !config.clientId) {
          setClientId(null)
          return
        }
        setClientId(config.clientId)
      } catch {
        if (!cancelled) {
          setClientId(null)
          setFailed(true)
        }
      }
    }
    void loadConfig()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!clientId) return
    const resolvedClientId = clientId
    let cancelled = false

    async function waitForWidth(host: HTMLElement): Promise<number> {
      for (let i = 0; i < 12; i += 1) {
        const w = Math.floor(host.getBoundingClientRect().width || host.clientWidth || 0)
        if (w >= 200) return Math.min(360, Math.max(240, w))
        await new Promise<void>((r) => requestAnimationFrame(() => r()))
      }
      return 320
    }

    async function render() {
      try {
        // iOS/Android installed PWA (standalone) often cannot load GSI iframe ("Load failed").
        const standalone =
          window.matchMedia('(display-mode: standalone)').matches ||
          // @ts-expect-error iOS Safari legacy
          Boolean(window.navigator.standalone)
        if (standalone) {
          if (!cancelled) {
            setReady(false)
            setFailed(true)
          }
          return
        }

        await loadGisScript()
        // Wait a frame so hostRef is mounted after clientId state commit.
        await new Promise<void>((r) => requestAnimationFrame(() => r()))
        if (cancelled) return
        const host = hostRef.current
        if (!host || !window.google?.accounts?.id) {
          if (!cancelled) setFailed(true)
          return
        }

        window.google.accounts.id.initialize({
          client_id: resolvedClientId,
          callback: (response) => {
            const credential = response.credential?.trim()
            if (!credential) {
              errorRef.current?.('Google sign-in failed')
              return
            }
            void Promise.resolve(callbackRef.current(credential)).catch((err) => {
              errorRef.current?.(
                err instanceof Error ? err.message : 'Google sign-in failed',
              )
            })
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: true,
        })

        host.innerHTML = ''
        const width = await waitForWidth(host)
        if (cancelled) return
        try {
          window.google.accounts.id.renderButton(host, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: mode === 'signin' ? 'signin_with' : 'signup_with',
            shape: 'rectangular',
            width,
            logo_alignment: 'left',
            locale: 'en',
          })
        } catch {
          // Some GSI builds reject width; retry without it.
          host.innerHTML = ''
          window.google.accounts.id.renderButton(host, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: mode === 'signin' ? 'signin_with' : 'signup_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            locale: 'en',
          })
        }
        if (!cancelled) {
          setReady(true)
          setFailed(false)
        }
      } catch {
        if (!cancelled) {
          setReady(false)
          setFailed(true)
        }
      }
    }

    void render()
    return () => {
      cancelled = true
    }
  }, [clientId, mode])

  if (!clientId) return null

  const dividerEl =
    divider === 'none' ? null : (
      <div className="cp-auth__divider" role="separator" aria-label="or">
        <span>or</span>
      </div>
    )

  return (
    <>
      {divider === 'above' ? dividerEl : null}
      <div className={`cp-google${disabled ? ' cp-google--disabled' : ''}`} aria-busy={!ready}>
        <div ref={hostRef} className="cp-google__btn" />
        {!ready && !failed ? <p className="cp-muted cp-google__loading">Loading Google…</p> : null}
        {failed ? (
          <p className="cp-muted cp-google__loading">
            Google button unavailable on this mobile app view. Use email and password below.
          </p>
        ) : null}
      </div>
      {divider === 'below' ? dividerEl : null}
    </>
  )
}
